/* WeQuote Dev Board — Release Notes (Lee, 3 Sep).
 * PublishNoteModal: the pop-up behind "Publish to Updates Page" on the
 * Completed page — Module, Title, Date, Long Description, Image. Publishing
 * stores a release note (server) AND stamps the card published.
 * ReleaseNotesView: the internal preview of the customer-facing page —
 * sticky header, quarter + module filters, "Copy embed link" for the public
 * embed served at /api/devboard/release-notes/embed. */
import React, { useEffect, useRef, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as LV from '../core/live'
import type { Feature } from '../core/types'
import { MODULE_OPTS } from '../core/constants'
import { todayISO } from '../core/dates'
import { fetchReleaseNotes, createReleaseNote, updateReleaseNote, deleteReleaseNote, type ReleaseNote } from '../core/api'
import { IcX } from '../icons'

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const fmtLong = (iso: string) => { const d = new Date(iso + 'T12:00:00'); return d.getDate() + ' ' + MONTHS[d.getMonth()] + ', ' + d.getFullYear() }
const quarterOf = (iso: string) => { const d = new Date(iso + 'T12:00:00'); return 'Q' + (Math.floor(d.getMonth() / 3) + 1) + ' ' + d.getFullYear() }

/* per-module pill colours — shared language with the board's chips */
export const MODULE_TONES: Record<string, [string, string]> = {
  'CRM': ['#e0e7ff', '#4338ca'],
  'Quoting & Proposals': ['#dbeafe', '#1d4ed8'],
  'Accounting': ['#dcfce7', '#15803d'],
  'Project Management & Snagg': ['#fef3c7', '#b45309'],
  'Product Log & Procurement': ['#ffedd5', '#c2410c'],
  'Inventory': ['#ccfbf1', '#0f766e'],
  'Other': ['#f3f4f6', '#4b5563'],
  'Internal Functionality': ['#f5f3ff', '#6d28d9'],
}
const modTone = (m: string): [string, string] => MODULE_TONES[m] || ['#1a1a2e', '#ffffff']
/** A note can carry several modules (Lee, 4 Sep) — stored comma-joined. */
export const splitMods = (m: string | null | undefined): string[] => String(m || '').split(',').map((t) => t.trim()).filter(Boolean)

/** Create mode (feat): "Publish to Updates Page" from Completed.
 *  Edit mode (edit): pencil on a Release Notes entry — same popup, saves in place. */
export const PublishNoteModal: React.FC<{ feat?: Feature; edit?: ReleaseNote; onClose: () => void; onSaved?: () => void }> = ({ feat, edit, onClose, onSaved }) => {
  const { mutate, showToast } = useDevBoard()
  const [mods, setMods] = useState<string[]>(edit ? splitMods(edit.module) : feat && feat.module ? [feat.module] : [])
  const toggleMod = (m: string) => setMods((xs) => xs.indexOf(m) >= 0 ? xs.filter((x) => x !== m) : [...xs, m])
  const [title, setTitle] = useState(edit ? edit.title : ((feat && feat.customerName) || '').trim() || (feat ? feat.title : ''))
  const [date, setDate] = useState(edit ? edit.date : (feat && feat.completedOn) || todayISO())
  const [body, setBody] = useState(edit ? edit.body : ((feat && feat.customerDesc) || '').trim() || ((feat && feat.notes) || ''))
  const [image, setImage] = useState<string | null>(edit ? edit.image : null)
  const [busy, setBusy] = useState(false)
  const pickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    if (file.size > 2.5 * 1024 * 1024) { showToast('That image is over 2.5 MB — please use a smaller one.'); return }
    const r = new FileReader()
    r.onload = () => setImage(String(r.result))
    r.readAsDataURL(file)
  }
  const publish = async () => {
    if (!title.trim() || !date || !body.trim()) { showToast('Title, date and the long description are all needed.'); return }
    setBusy(true)
    try {
      if (edit) {
        await updateReleaseNote(edit.id, { module: mods.join(','), title: title.trim(), date, body: body.trim(), image })
        showToast('“' + title.trim() + '” updated.')
      } else if (feat) {
        await createReleaseNote({ cardId: feat.id, module: mods.join(','), title: title.trim(), date, body: body.trim(), image })
        if (!feat.publishedOn) mutate((fs) => LV.publishFeature(fs, feat.id))
        showToast('“' + title.trim() + '” published to the Release Notes page.')
      }
      if (onSaved) onSaved()
      onClose()
    } catch (e: any) { showToast((edit ? 'Save' : 'Publish') + ' failed: ' + e.message) } finally { setBusy(false) }
  }
  return (
    <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) onClose() }}>
      <div className="modal" style={{ width: 560 }}>
        <h2>{edit ? 'Edit Release Note' : 'Publish to Updates Page'}</h2>
        <div className="modal-sub">{edit ? 'Changes apply to the customer-facing page (and the WeQuote embed) straight away.' : <>This entry appears on the customer-facing <strong>Release Notes</strong> page (and the WeQuote embed).</>}</div>
        <div className="fg"><label>Module(s) <span className="rnp-multi">(pick several — none = Platform-wide)</span></label>
          <select value="" onChange={(e) => { if (e.target.value) toggleMod(e.target.value) }}>
            <option value="">Add a module…</option>
            {MODULE_OPTS.filter((m) => mods.indexOf(m) === -1).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {mods.length > 0 && (
            <div className="pf-modchips">
              {mods.map((m) => (
                <span key={m} className="rnv-mod rnp-chip" style={{ background: modTone(m)[0], color: modTone(m)[1] }}>{m.toUpperCase()}<b title="Remove" onClick={() => toggleMod(m)}><IcX size={11} /></b></span>
              ))}
            </div>
          )}
        </div>
        <div className="fg"><label>Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} /></div>
        <div className="fg"><label>Date *</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="fg"><label>Long description *</label><textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What shipped, what it does for the customer, how to find it…" /></div>
        <div className="fg"><label>Image</label>
          <input type="file" accept="image/*" onChange={pickImage} />
          {image && <div className="rn-imgprev"><img src={image} alt="" /><button className="btn-s" onClick={() => setImage(null)}>Remove</button></div>}
        </div>
        <div className="factions"><button className="btn-s" onClick={onClose}>Cancel</button><button className="btn-p" disabled={busy} onClick={() => { void publish() }}>{busy ? (edit ? 'Saving…' : 'Publishing…') : (edit ? 'Save changes' : 'Publish')}</button></div>
      </div>
    </div>
  )
}

export const ReleaseNotesView: React.FC = () => {
  const { mutate, cards, showToast } = useDevBoard()
  const [notes, setNotes] = useState<ReleaseNote[]>([])
  const [loadingN, setLoadingN] = useState(true)
  const [fq, setFq] = useState('')
  const [fm, setFm] = useState('')
  const [page, setPage] = useState(1)
  const [editFor, setEditFor] = useState<ReleaseNote | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const reload = () => { fetchReleaseNotes().then((n) => { setNotes(n); setLoadingN(false) }).catch(() => setLoadingN(false)) }
  useEffect(() => { reload() }, [])
  /* Geist just for this page (Lee, 4 Sep) — loaded once, on demand */
  useEffect(() => {
    if (document.getElementById('geist-font')) return
    const l = document.createElement('link')
    l.id = 'geist-font'; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800&display=swap'
    document.head.appendChild(l)
  }, [])
  const quarters = Array.from(new Set(notes.map((n) => quarterOf(n.date))))
  const modules = Array.from(new Set(notes.flatMap((n) => splitMods(n.module))))
  const shown = notes.filter((n) => (!fq || quarterOf(n.date) === fq) && (!fm || splitMods(n.module).indexOf(fm) >= 0))
  /* pagination (Lee, 4 Sep): 20 per page, over the FILTERED list */
  const PAGE_SIZE = 20
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE))
  const pg = Math.min(page, pages)
  const pageItems = shown.slice((pg - 1) * PAGE_SIZE, pg * PAGE_SIZE)
  const goPage = (n: number) => { setPage(Math.min(Math.max(1, n), pages)); if (scrollRef.current) scrollRef.current.scrollTop = 0 }
  const embedUrl = window.location.origin + '/api/devboard/release-notes/embed'
  const copyEmbed = () => {
    navigator.clipboard.writeText(embedUrl).then(() => showToast('Embed link copied — paste it into the WeQuote Updates page (iframe).'))
      .catch(() => showToast('Couldn’t copy — the embed link is: ' + embedUrl))
  }
  const del = async (n: ReleaseNote) => {
    if (!window.confirm('Delete “' + n.title + '” from the Release Notes page (and the embed)? This can’t be undone.')) return
    const r = await deleteReleaseNote(n.id)
    // published state on the Completed page is linked to the actual entry —
    // deleting the entry clears the card's "Published" stamp too
    if (r.cardId != null && cards.some((f) => f.id === r.cardId && f.publishedOn)) {
      const cid = r.cardId
      mutate((fs) => LV.publishFeature(fs, cid))
    }
    reload()
  }
  return (
    <div id="relNotesView">
      <div className="rnv-hd">
        <div>
          <h2 className="rnv-title">WeQuote Updates and Release Notes</h2>
          <div className="rnv-sub">The latest updates to improve how you quote, manage projects, and deliver work.</div>
        </div>
        <div className="rnv-tools">
          <select value={fq} onChange={(e) => { setFq(e.target.value); setPage(1) }}>
            <option value="">All quarters</option>
            {quarters.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={fm} onChange={(e) => { setFm(e.target.value); setPage(1) }}>
            <option value="">All modules</option>
            {modules.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <button className="mode-btn" onClick={copyEmbed}>Copy embed link</button>
        </div>
      </div>
      <div className="rnv-scroll" ref={scrollRef}>
        {loadingN ? <div className="rnv-empty">Loading…</div> : shown.length === 0 ? (
          <div className="rnv-empty">Nothing published{fq || fm ? ' for this filter' : ' yet — use “Publish to Updates Page” on a completed feature'}.</div>
        ) : pageItems.map((n) => (
          <article key={n.id} className="rnv-item">
            <div className="rnv-rail"><span className="rnv-dot" /></div>
            <div className="rnv-side">
              <div className="rnv-date">{fmtLong(n.date)}</div>
              <h3 className="rnv-t">{n.title}</h3>
              <div className="rnv-mods">
                {(splitMods(n.module).length ? splitMods(n.module) : ['']).map((m) => (
                  <span key={m || 'pw'} className="rnv-mod" style={{ background: modTone(m)[0], color: modTone(m)[1] }}>{(m || 'Platform-wide').toUpperCase()}</span>
                ))}
              </div>
              <div className="rnv-actions">
                <button className="rnv-edit" title="Edit this entry" onClick={() => setEditFor(n)}>Edit</button>
                <button className="rnv-del" title="Delete from the Release Notes page" onClick={() => { void del(n) }}>Delete</button>
              </div>
            </div>
            <div className="rnv-body">
              <div className="rnv-text">{n.body}</div>
              {n.image && <img className="rnv-img" src={n.image} alt="" />}
            </div>
          </article>
        ))}
      </div>
      {editFor && <PublishNoteModal edit={editFor} onClose={() => setEditFor(null)} onSaved={reload} />}
      {shown.length > PAGE_SIZE && (
        <div className="rnv-pager">
          <button className="rnv-pgbtn" disabled={pg === 1} onClick={() => goPage(pg - 1)}>&lsaquo; Prev</button>
          <div className="rnv-pgnums">
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button key={n} className={'rnv-pgnum' + (n === pg ? ' on' : '')} onClick={() => goPage(n)}>{n}</button>
            ))}
          </div>
          <button className="rnv-pgbtn" disabled={pg === pages} onClick={() => goPage(pg + 1)}>Next &rsaquo;</button>
        </div>
      )}
    </div>
  )
}
